import { OrderStatus } from '../../proto/generated/order'

export type TrackerAction = {
    key: string
    label: string
    kind: 'modify' | 'cancel'
    /** Text for the assistant. Cancel is sent (the reply is still a confirm-first proposal); modify prefills. */
    prompt: string
    send: boolean
}

export type TrackerActionInput = {
    orderId?: string | null
    status?: OrderStatus | null
    /** Display order type: MARKET, LIMIT, SL, SL-M (hyphen or underscore). */
    orderType: string
    /** Pending conditional entries can be cancelled from chat but not modified yet. */
    conditional?: boolean
    /** Bracket entry legs can't be cancelled or modified on their own. */
    bracketEntry?: boolean
    /** Prefix for multi-leg rows, e.g. "stop" -> "Cancel stop". */
    roleLabel?: string
}

const CANCELLABLE = new Set<OrderStatus>([OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING, OrderStatus.INACTIVE])

const MODIFIABLE = new Set<OrderStatus>([OrderStatus.OPEN, OrderStatus.PENDING, OrderStatus.TRIGGER_PENDING])

/**
 * Buttons for a live order, mirroring what the assistant's MODIFY / CANCEL path accepts server-side
 * (AiOrderActionPolicy). Both go through chat, so the user still reviews a proposal card before anything
 * reaches the exchange.
 */
export function trackerActionsForOrder(input: TrackerActionInput): TrackerAction[] {
    const orderId = input.orderId?.trim()
    if (!orderId || input.status == null || input.bracketEntry) return []
    const suffix = input.roleLabel ? ` ${input.roleLabel}` : ''
    const typeKey = input.orderType.trim().toUpperCase().replace(/-/g, '_')
    const actions: TrackerAction[] = []

    const patchField = typeKey === 'LIMIT' ? 'limit' : typeKey === 'SL' || typeKey === 'SL_M' ? 'trigger' : null
    if (MODIFIABLE.has(input.status) && !input.conditional && patchField) {
        actions.push({
            key: `modify-${orderId}`,
            label: `Modify${suffix}`,
            kind: 'modify',
            prompt: `Modify order ${orderId} ${patchField} to `,
            send: false
        })
    }
    if (CANCELLABLE.has(input.status)) {
        actions.push({
            key: `cancel-${orderId}`,
            label: `Cancel${suffix}`,
            kind: 'cancel',
            prompt: `Cancel order ${orderId}`,
            send: true
        })
    }
    return actions
}

const PRODUCT_WORD: Record<string, string> = {
    INTRADAY: 'intraday',
    CNC: 'delivery',
    MARGIN: 'overnight',
    MTF: 'MTF',
}

/**
 * "Exit" shortcut on a position row. Sends a square-off request naming the product, so the assistant
 * doesn't have to ask which one when both intraday and delivery positions exist. The reply is an exit
 * proposal card the user still confirms.
 */
/** Position-row buttons: prefill a stop price, or send a square-off request. */
export function positionActions(position: {
    securityCode: string
    product: string
    netQuantity: number
}): TrackerAction[] {
    const exit = exitActionForPosition(position)
    if (exit.length === 0) return []
    const productWord = PRODUCT_WORD[position.product.toUpperCase()]
    const scope = `my ${productWord ? `${productWord} ` : ''}${position.securityCode} position`
    return [
        {
            key: `protect-${position.securityCode}-${position.product}`,
            label: 'Add stop',
            kind: 'modify',
            prompt: `Add a stop to ${scope} at `,
            send: false,
        },
        ...exit,
    ]
}

export function exitActionForPosition(position: {
    securityCode: string
    product: string
    netQuantity: number
}): TrackerAction[] {
    if (!position.securityCode || position.netQuantity === 0) return []
    const productWord = PRODUCT_WORD[position.product.toUpperCase()]
    return [
        {
            key: `exit-${position.securityCode}-${position.product}`,
            label: 'Exit',
            kind: 'cancel',
            prompt: `Exit my ${productWord ? `${productWord} ` : ''}${position.securityCode} position`,
            send: true,
        },
    ]
}
